// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import Link from 'next/link';
import styled from 'styled-components';

export const Footer = styled.footer`
  position: relative;
  background:
    radial-gradient(1.5px 1.5px at 10% 15%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 22% 55%, rgba(255, 255, 255, 0.35), transparent),
    radial-gradient(1.5px 1.5px at 37% 25%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1px 1px at 52% 70%, rgba(255, 255, 255, 0.3), transparent),
    radial-gradient(1.5px 1.5px at 66% 12%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 78% 48%, rgba(255, 255, 255, 0.35), transparent),
    radial-gradient(1.5px 1.5px at 90% 28%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1px 1px at 45% 90%, rgba(255, 255, 255, 0.25), transparent),
    ${({ theme }) => theme.colors.ink};
`;

export const Inner = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 56px 20px 40px;

  ${({ theme }) => theme.breakpoints.desktop} {
    grid-template-columns: 1.4fr 0.8fr 1fr 1.3fr;
    gap: 48px;
    padding: 72px 32px 48px;
  }
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 14px;
`;

export const BrandLogo = styled.img.attrs({
  src: '/images/favicon.png',
  alt: '',
})`
  width: 26px;
  height: 26px;
`;

export const BrandName = styled.span`
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.white};
`;

export const Blurb = styled.p`
  margin: 0 0 18px;
  max-width: 340px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
`;

export const Socials = styled.div`
  display: flex;
  gap: 10px;
`;

export const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.75);

  svg {
    fill: rgba(255, 255, 255, 0.75);
  }

  img {
    filter: grayscale(1) brightness(1.9);
    opacity: 0.75;
    transition: filter 0.15s ease, opacity 0.15s ease;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.grafanaOrangeLight};

    svg {
      fill: ${({ theme }) => theme.colors.grafanaOrangeLight};
    }

    img {
      filter: none;
      opacity: 1;
    }
  }
`;

export const ColumnTitle = styled.p`
  margin: 0 0 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`;

export const ColumnLink = styled(Link)`
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  text-transform: capitalize;

  &:hover {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const ColumnText = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.6);
  overflow-wrap: anywhere;

  a {
    color: rgba(255, 255, 255, 0.75);
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.colors.grafanaOrangeLight};
    }
  }
`;

export const NewsletterCopy = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
`;

export const NewsletterForm = styled.form`
  display: flex;
  align-items: center;
  max-width: 340px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  overflow: hidden;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const NewsletterInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 18px;
  border: none;
  background: none;
  font-size: 14px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.white};
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const NewsletterSubmit = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  background: none;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const NewsletterThanks = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grafanaOrangeLight};
`;

export const BottomBar = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

export const BottomBarInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 20px 32px;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
  }
`;
